import * as React from "react";
import { useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddCircle as AddIcon,
  Bolt as BoltIcon,
  Category as CategoryIcon,
  Cloud as CloudIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  GridView as GridViewIcon,
  Launch as LaunchIcon,
  Layers as LayersIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  SettingsEthernet as SettingsEthernetIcon,
  Storage as StorageIcon,
  ViewList as ViewListIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

/**
 * Randol Subdomains Dashboard – Material UI v6
 * --------------------------------------------
 * A single-file React component that renders a home dashboard:
 * Subdomains -> Contexts, with KPIs, search, grid/list toggle,
 * and expandable context preview rows. Uses only MUI v6.
 */

// ----------------------------
// Types
// ----------------------------
export type DomainModel = { id: string; name: string };
export type Service = {
  id: string;
  name: string;
  operations: number;
  handlesEvents: number;
  publishesEvents: number;
};
export type Context = {
  id: string;
  name: string;
  description?: string;
  domainModels?: DomainModel[];
  services?: Service[];
  tags?: string[];
  environment?: "prod" | "staging" | "dev";
  lastActivity?: string; // ISO
};
export type Subdomain = { id: string; name: string; description?: string; contexts: Context[] };

// ----------------------------
// Utilities
// ----------------------------
const pluralize = (n: number, s: string) => `${n} ${s}${n === 1 ? "" : "s"}`;
const countModels = (c?: Context) => c?.domainModels?.length ?? 0;
const countServices = (c?: Context) => c?.services?.length ?? 0;
const countOps = (c?: Context) => c?.services?.reduce((a, s) => a + (s.operations || 0), 0) ?? 0;
const countHandled = (c?: Context) => c?.services?.reduce((a, s) => a + (s.handlesEvents || 0), 0) ?? 0;
const countPublished = (c?: Context) => c?.services?.reduce((a, s) => a + (s.publishesEvents || 0), 0) ?? 0;

// ----------------------------
// Mock Data
// ----------------------------
const MOCK: Subdomain[] = [
  {
    id: "sd-customer",
    name: "Customer Management",
    description: "Accounts, profiles, KYC and loyalty.",
    contexts: [
      {
        id: "ctx-cust-crm",
        name: "CRM Core",
        description: "Customer master data & profile orchestration",
        environment: "prod",
        tags: ["core", "PII"],
        lastActivity: new Date().toISOString(),
        domainModels: [
          { id: "m1", name: "Customer" },
          { id: "m2", name: "Address" },
          { id: "m3", name: "LoyaltyWallet" },
        ],
        services: [
          { id: "s1", name: "ProfileService", operations: 8, handlesEvents: 3, publishesEvents: 2 },
          { id: "s2", name: "LoyaltyService", operations: 6, handlesEvents: 2, publishesEvents: 4 },
        ],
      },
      {
        id: "ctx-cust-analytics",
        name: "Customer Analytics",
        description: "Segmentation & churn models",
        environment: "staging",
        tags: ["ml"],
        lastActivity: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
        domainModels: [{ id: "m4", name: "Segment" }],
        services: [{ id: "s3", name: "SegmentService", operations: 4, handlesEvents: 1, publishesEvents: 1 }],
      },
    ],
  },
  {
    id: "sd-payments",
    name: "Payments",
    description: "Authorisations, settlements and refunds.",
    contexts: [
      {
        id: "ctx-pay-gateway",
        name: "Payment Gateway",
        description: "Card & alternate payments routing",
        environment: "prod",
        tags: ["pci", "high-throughput"],
        lastActivity: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
        domainModels: [
          { id: "m5", name: "Authorisation" },
          { id: "m6", name: "Settlement" },
        ],
        services: [
          { id: "s4", name: "AuthService", operations: 10, handlesEvents: 5, publishesEvents: 3 },
          { id: "s5", name: "SettlementService", operations: 7, handlesEvents: 2, publishesEvents: 2 },
        ],
      },
      {
        id: "ctx-pay-refunds",
        name: "Refunds",
        description: "Customer-initiated refunds",
        environment: "dev",
        tags: ["backoffice"],
        lastActivity: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
        domainModels: [{ id: "m7", name: "Refund" }],
        services: [{ id: "s6", name: "RefundService", operations: 5, handlesEvents: 1, publishesEvents: 1 }],
      },
    ],
  },
  {
    id: "sd-ops",
    name: "Operations",
    description: "Operational tooling and orchestration.",
    contexts: [
      {
        id: "ctx-ops-orchestrator",
        name: "Orchestrator",
        description: "Long-running workflows",
        environment: "prod",
        tags: ["saga", "events"],
        lastActivity: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        domainModels: [{ id: "m8", name: "Workflow" }],
        services: [{ id: "s7", name: "SagaService", operations: 9, handlesEvents: 7, publishesEvents: 4 }],
      },
    ],
  },
];

// ----------------------------
// Styled helpers
// ----------------------------
const KPI = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number | string }) => (
  <Paper elevation={0} variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderRadius: 3 }}>
    <Box sx={{ p: 1, borderRadius: 2, border: 1, borderColor: "divider", display: "inline-flex" }}>
      <Icon />
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={600} lineHeight={1.2}>{value}</Typography>
    </Box>
  </Paper>
);

const ExpandIconButton = styled((props: any) => <IconButton {...props} />)(({ theme, expand }: any) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
}));

function EnvChip({ env }: { env?: Context["environment"] }) {
  if (!env) return null;
  const color = env === "prod" ? "success" : env === "staging" ? "warning" : "default";
  const label = env === "prod" ? "PROD" : env === "staging" ? "STAGE" : "DEV";
  return <Chip size="small" color={color as any} label={label} variant={env === "dev" ? "outlined" : "filled"} />;
}

function StatPill({ icon: Icon, label, value, title }: { icon: React.ElementType; label: string; value: number | string; title?: string }) {
  return (
    <Tooltip title={title || ""}>
      <Chip
        size="small"
        icon={<Icon fontSize="small" />}
        label={
          <Box component="span" sx={{ display: "inline-flex", gap: 0.5 }}>
            <Typography component="span" fontWeight={600}>{value}</Typography>
            <Typography component="span" color="text.secondary">{label}</Typography>
          </Box>
        }
        variant="outlined"
        sx={{ borderRadius: 9999 }}
      />
    </Tooltip>
  );
}

// ----------------------------
// Context Row
// ----------------------------
function ContextRow({ ctx, onOpen }: { ctx: Context; onOpen?: () => void }) {
  const models = countModels(ctx);
  const services = countServices(ctx);
  const ops = countOps(ctx);
  const handled = countHandled(ctx);
  const published = countPublished(ctx);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight={600} sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <LayersIcon fontSize="small" /> {ctx.name}
          </Typography>
          <EnvChip env={ctx.environment} />
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {ctx.tags?.slice(0, 3).map((t) => (
              <Chip key={t} size="small" label={t} variant="outlined" />
            ))}
            {ctx.tags && ctx.tags.length > 3 && <Chip size="small" label={`+${ctx.tags.length - 3}`} variant="outlined" />}
          </Stack>
        </Stack>
        {ctx.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {ctx.description}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
          <Chip size="small" variant="outlined" label={pluralize(models, "model")} />
          <Chip size="small" variant="outlined" label={pluralize(services, "service")} />
          <Chip size="small" variant="outlined" label={pluralize(ops, "operation")} />
          <Chip size="small" variant="outlined" label={`${handled} handles`} />
          <Chip size="small" variant="outlined" label={`${published} publishes`} />
          {ctx.lastActivity && (
            <Chip size="small" variant="outlined" icon={<EventIcon />} label={new Date(ctx.lastActivity).toLocaleString()} />
          )}
        </Stack>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="outlined" size="small" endIcon={<LaunchIcon />} onClick={onOpen}>Open</Button>
      </Stack>
    </Paper>
  );
}

// ----------------------------
// Subdomain Card
// ----------------------------
function SubdomainCard({
  subdomain,
  expanded,
  onToggleExpand,
  onOpenContext,
  onCreateContext,
}: {
  subdomain: Subdomain;
  expanded: boolean;
  onToggleExpand: (id: string) => void;
  onOpenContext?: (ctx: Context, subdomain: Subdomain) => void;
  onCreateContext?: (subdomain: Subdomain) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const models = subdomain.contexts.reduce((a, c) => a + countModels(c), 0);
  const services = subdomain.contexts.reduce((a, c) => a + countServices(c), 0);
  const ops = subdomain.contexts.reduce((a, c) => a + countOps(c), 0);
  const handled = subdomain.contexts.reduce((a, c) => a + countHandled(c), 0);
  const published = subdomain.contexts.reduce((a, c) => a + countPublished(c), 0);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardHeader
        sx={{ pb: 0.5 }}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <CategoryIcon fontSize="small" />
            <Typography variant="h6">{subdomain.name}</Typography>
          </Stack>
        }
        subheader={subdomain.description}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={expanded ? "Collapse" : "Expand"}>
              <ExpandIconButton
                aria-label="expand"
                expand={expanded ? 1 : 0}
                onClick={() => onToggleExpand(subdomain.id)}
              >
                <ExpandMoreIcon />
              </ExpandIconButton>
            </Tooltip>
            <IconButton aria-label="more" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { setAnchorEl(null); onCreateContext?.(subdomain); }}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} /> New context
              </MenuItem>
            </Menu>
          </Stack>
        }
      />
      <CardContent sx={{ pt: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <StatPill icon={FolderIcon} label="contexts" value={subdomain.contexts.length} />
          <StatPill icon={StorageIcon} label="models" value={models} />
          <StatPill icon={CloudIcon} label="services" value={services} />
          <StatPill icon={BoltIcon} label="ops" value={ops} title="Operations" />
          <StatPill icon={SettingsEthernetIcon} label="handles" value={handled} title="Events handled" />
          <StatPill icon={SettingsEthernetIcon} label="publishes" value={published} title="Events published" />
        </Stack>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {subdomain.contexts.map((ctx) => (
              <Grid key={ctx.id} item xs={12} md={6}>
                <ContextRow ctx={ctx} onOpen={() => onOpenContext?.(ctx, subdomain)} />
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ----------------------------
// Main Dashboard
// ----------------------------
export default function DashboardView({
  subdomains = MOCK,
  onOpenContext,
  onCreateSubdomain,
  onCreateContext,
}: {
  subdomains?: Subdomain[];
  onOpenContext?: (ctx: Context, subdomain: Subdomain) => void;
  onCreateSubdomain?: () => void;
  onCreateContext?: (subdomain: Subdomain) => void;
}) {
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subdomains;
    return subdomains
      .map((sd) => ({
        ...sd,
        contexts: sd.contexts.filter((c) =>
          [sd.name, sd.description || "", c.name, c.description || "", ...(c.tags || [])]
            .join(" ")
            .toLowerCase()
            .includes(q)
        ),
      }))
      .filter((sd) => sd.contexts.length > 0 || sd.name.toLowerCase().includes(q));
  }, [subdomains, query]);

  const totals = useMemo(() => {
    const all = subdomains.flatMap((sd) => sd.contexts);
    return {
      subdomains: subdomains.length,
      contexts: all.length,
      models: all.reduce((a, c) => a + countModels(c), 0),
      services: all.reduce((a, c) => a + countServices(c), 0),
      operations: all.reduce((a, c) => a + countOps(c), 0),
      events: all.reduce((a, c) => a + countHandled(c) + countPublished(c), 0),
    };
  }, [subdomains]);

  const toggleExpand = (id: string) => setExpanded((p) => (p === id ? null : id));

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" sx={{ py: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Randol – Subdomains</Typography>
              <Typography variant="body2" color="text.secondary">Browse business subdomains, drill into contexts, and jump into your work.</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search subdomains, contexts, tags…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              />
              <Tabs value={view} onChange={(_, v) => setView(v)} aria-label="view switch" textColor="primary" indicatorColor="primary">
                <Tab icon={<GridViewIcon />} iconPosition="start" value="grid" label="Grid" />
                <Tab icon={<ViewListIcon />} iconPosition="start" value="list" label="List" />
              </Tabs>
              <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateSubdomain}>New subdomain</Button>
            </Stack>
          </Stack>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* KPI Strip */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={4} lg={2}><KPI icon={CategoryIcon} label="Subdomains" value={totals.subdomains} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KPI icon={FolderIcon} label="Contexts" value={totals.contexts} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KPI icon={StorageIcon} label="Models" value={totals.models} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KPI icon={CloudIcon} label="Services" value={totals.services} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KPI icon={BoltIcon} label="Operations" value={totals.operations} /></Grid>
          <Grid item xs={6} md={4} lg={2}><KPI icon={SettingsEthernetIcon} label="Events" value={totals.events} /></Grid>
        </Grid>

        {/* Content */}
        {view === "grid" ? (
          <Grid container spacing={2}>
            {filtered.map((sd) => (
              <Grid item key={sd.id} xs={12} md={6}>
                <SubdomainCard
                  subdomain={sd}
                  expanded={expanded === sd.id}
                  onToggleExpand={toggleExpand}
                  onOpenContext={onOpenContext}
                  onCreateContext={onCreateContext}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Stack spacing={2}>
            {filtered.map((sd) => (
              <Card key={sd.id} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardHeader
                  sx={{ pb: 0.5 }}
                  title={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CategoryIcon fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={700}>{sd.name}</Typography>
                    </Stack>
                  }
                  subheader={sd.description}
                  action={
                    <Tooltip title={expanded === sd.id ? "Collapse" : "Expand"}>
                      <ExpandIconButton aria-label="expand" expand={expanded === sd.id ? 1 : 0} onClick={() => toggleExpand(sd.id)}>
                        <ExpandMoreIcon />
                      </ExpandIconButton>
                    </Tooltip>
                  }
                />
                <CardContent sx={{ pt: 1 }}>
                  <Collapse in={expanded === sd.id} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      {sd.contexts.map((ctx) => (
                        <Grid key={ctx.id} item xs={12} md={6}>
                          <ContextRow ctx={ctx} onOpen={() => onOpenContext?.(ctx, sd)} />
                        </Grid>
                      ))}
                    </Grid>
                  </Collapse>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Tips */}
        <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Tips</Typography>
          <Box component="ul" sx={{ pl: 3, m: 0, color: "text.secondary" }}>
            <li>Use the search box to find subdomains, contexts, or tags.</li>
            <li>Switch between Grid and List views depending on how many contexts you have.</li>
            <li>Click a subdomain to expand and open a specific context.</li>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
